import React from "react";
import styled from "styled-components";
import { Link } from "gatsby";
import { getImage, GatsbyImage } from "gatsby-plugin-image";
import Box from "react-raster";
import { H1, Copy } from "../Typography";
import Arrow, { ArrowContainer } from "../Arrow";
import Container from "../Container";

const FullWidthWrapper = styled.div`
  position: relative;
  width: 100%;
  background-color: ${({ theme, $backgroundColour }) =>
    theme.colours[$backgroundColour]};
  margin-top: 0;
  height: 812px;
  margin-top: -92px;
  overflow: hidden;
  margin-bottom: ${({ $hasBottomMargin }) =>
    ($hasBottomMargin && "35px") || "0px"};

  @media (${({ theme }) => theme.mq.m}) {
    margin-top: -100px;
    height: 728px;
    margin-bottom: ${({ $hasBottomMargin }) =>
      ($hasBottomMargin && "100px") || "0px"};
  }

  @media (${({ theme }) => theme.mq.l}) {
    margin-top: -100px;
    margin-bottom: ${({ $hasBottomMargin }) =>
      ($hasBottomMargin && "100px") || "0px"};
  }
`;

const BackgroundImageContainer = styled.div`
  width: 100%;
  position: absolute;
  left: 0;
  right: auto;
  overflow: hidden;
  bottom: 0;
  height: 586px;

  @media (${({ theme }) => theme.mq.m}) {
    right: 0;
    left: auto;
    width: 1186px;
    height: auto;
  }

  @media (${({ theme }) => theme.mq.l}) {
    right: 0;
    left: auto;
  }
`;

const Title = styled(H1)`
  color: ${({ theme, $colour }) => theme.colours[$colour]};
  font-weight: 400;
  font-size: 50px;
  line-height: 50px;
  margin-top: 140px;

  @media (${({ theme }) => theme.mq.m}) {
    font-size: 100px;
    line-height: 100px;
    font-weight: 500;
    margin-top: 180px;
  }

  @media (${({ theme }) => theme.mq.xl}) {
    font-size: 120px;
    line-height: 109px;
    font-weight: 500;
    margin-top: 273px;
  }
`;

const Description = styled(Copy)`
  font-size: 24px;
  line-height: 26px;

  @media (${({ theme }) => theme.mq.m}) {
    font-size: 32px;
    line-height: 33px;
  }
`;

const Cta = styled(Link)`
  color: ${({ theme }) => theme.colours.white};
  text-decoration: none;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  font-size: 11px;
  line-height: 12px;

  @media (${({ theme }) => theme.mq.m}) {
    font-size: 18px;
    line-height: 20px;
  }

  @media (${({ theme }) => theme.mq.l}) {
    font-size: 18px;
    line-height: 20px;
  }
`;

const CtaLabel = styled.div`
  margin-right: 10px;
`;

const CtaIcon = styled(ArrowContainer)`
  background-color: white;
`;

const SecondaryImageContainer = styled.div`
  position: absolute;
  overflow: hidden;
  width: 132px;
  height: 34px;
  bottom: 52px;
  left: 30px;
  right: auto;
  top: auto;

  @media (${({ theme }) => theme.mq.m}) {
    width: 132px;
    height: 34px;
    top: 120px;
    right: 30px;
    left: auto;
    bottom: auto;
  }

  @media (${({ theme }) => theme.mq.l}) {
    width: 252px;
    height: 65px;
    bottom: 89px;
    right: 88px;
    left: auto;
    top: auto;
  }
`;

function Jumbotron({
  backgroundColour,
  backgroundImage,
  secondaryImage,
  title,
  description,
  ctaLabel,
  ctaUrl,
  ctaChatbotIdentifier,
  hasBottomMargin,
}) {
  return (
    <FullWidthWrapper
      $backgroundColour={backgroundColour}
      $hasBottomMargin={hasBottomMargin}
    >
      <BackgroundImageContainer>
        <GatsbyImage image={getImage(backgroundImage)} />
      </BackgroundImageContainer>
      <Container>
        <Box cols={[6, 8]} marginLeft={[0, 1]} marginRight={[0, 3]}>
          <Title $colour="white">{title}</Title>
        </Box>
        <Box cols={[6, 8]} marginLeft={[0, 1]} marginRight={[0, 3]}>
          <Box cols={[6, 6, 5]}>
            <Description medium colour="white">
              {description}
            </Description>
          </Box>
          <Box cols={[6, 2, 3]}>
            <Cta
              to={ctaUrl}
              onClick={(event) => {
                if (ctaChatbotIdentifier) {
                  event.preventDefault();
                  window.LeadBooster && window.LeadBooster.trigger("open");
                }
              }}
            >
              <CtaLabel>{ctaLabel}</CtaLabel>
              <CtaIcon large>
                <Arrow colour="black" large transform="rotate(-90deg)" />
              </CtaIcon>
            </Cta>
          </Box>
        </Box>
      </Container>
      {secondaryImage && (
        <SecondaryImageContainer>
          <GatsbyImage image={getImage(secondaryImage)} />
        </SecondaryImageContainer>
      )}
    </FullWidthWrapper>
  );
}

export default Jumbotron;
